import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/** A completed hand card: what a player who has scrolled down reads. */
export const SCROLL_ANCHOR_SELECTOR = '[data-hand-card="completed"]';

/**
 * Where the list should scroll to so the anchor stays where it was on screen
 * after content above it grew or shrank by (`nextAnchorTop - previousAnchorTop`).
 * At the very top the list follows the newest row instead (Justin, 24 Sep:
 * "if it's at the top, it auto-scrolls, but if it's not… it's static").
 */
export const anchoredScrollTop = (
  scrollTop: number,
  previousAnchorTop: number,
  nextAnchorTop: number
) => (scrollTop <= 0 ? scrollTop : scrollTop + (nextAnchorTop - previousAnchorTop));

/** The unfold a card's body plays when it opens; CSS modules hash the keyframes' name around it. */
const isUnfold = (event: Event) =>
  String((event as AnimationEvent).animationName ?? '').includes('hh-body-open');

/** Whether any of these unfolding bodies sits above the anchor card (not inside it). */
const unfoldsAbove = (anchor: HTMLElement, bodies: Set<EventTarget>) =>
  Array.from(bodies).some(
    (body) =>
      body instanceof Node &&
      !anchor.contains(body) &&
      Boolean(anchor.compareDocumentPosition(body) & Node.DOCUMENT_POSITION_PRECEDING)
  );

/**
 * The completed card the reader is on: the first one still in view at the top
 * of the list (the last one when the list is scrolled past them all).
 */
const readingCard = (list: HTMLElement): HTMLElement | null => {
  const viewTop = list.getBoundingClientRect().top;
  const cards = Array.from(list.querySelectorAll<HTMLElement>(SCROLL_ANCHOR_SELECTOR));
  return cards.find((card) => card.getBoundingClientRect().bottom > viewTop) ?? cards.pop() ?? null;
};

/**
 * Keeps the hand a scrolled reader is on still while things above it change:
 * the live hand appearing, playing on (each action adds a row to its open
 * play-by-play) or clearing, the viewer's hole cards arriving in its header,
 * the live card opening or closing, a finished hand landing, an open hand
 * above growing as its document fills in. Without it each of those pushes the
 * cards a scrolled reader is looking at — the "I'm trying to click on the next
 * hand, I'm going to keep missing" complaint from the 24 Sep demo (Cord).
 * Browsers' own scroll anchoring is not relied on (Safari lacks it, and the
 * list opts out with `overflow-anchor: none` so Chrome cannot double-correct).
 *
 * The anchor is the completed card in view at the top of the list, followed by
 * identity: it is re-picked whenever the reader scrolls and after every
 * correction, so whatever changes above it — in any card — is measured on the
 * card being read itself. The check runs after every render of the list's
 * owner rather than on a list of causes, which kept missing some (the review
 * of #4016 found four). `offsetTop` is layout, not scroll, so a reader
 * scrolling between renders never reads as movement. At the very top the list
 * follows the newest instead, so a new hand appears.
 */
export const useScrollAnchor = (listRef: RefObject<HTMLElement>) => {
  const anchorRef = useRef<{ element: HTMLElement; top: number } | null>(null);
  // Bodies unfolding right now (HandCardBody's `hh-body-open`). An unfold is
  // the expansion the player asked for: the cards below the opened one slide
  // down under a header useStableToggle holds. So while one runs ABOVE the
  // anchor (the live card, opened over the completed hands), nothing is
  // corrected, and when it ends the anchor is re-picked on the settled layout,
  // rather than a later render scrolling the whole body height away. An unfold
  // below the anchor, or inside it, cannot move it, so corrections go on.
  const unfoldingRef = useRef(new Set<EventTarget>());

  const pickAnchor = useCallback(() => {
    const list = listRef.current;
    const element = list ? readingCard(list) : null;
    anchorRef.current = element ? { element, top: element.offsetTop } : null;
  }, [listRef]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    // A body closed or unmounted mid-unfold never reports its end to the list.
    unfoldingRef.current.forEach((body) => {
      if (!(body instanceof Node) || !list.contains(body)) unfoldingRef.current.delete(body);
    });
    const previous = anchorRef.current;
    if (previous && list.contains(previous.element)) {
      if (unfoldsAbove(previous.element, unfoldingRef.current)) return;
      const top = previous.element.offsetTop;
      if (top !== previous.top) {
        list.scrollTop = anchoredScrollTop(list.scrollTop, previous.top, top);
      }
    }
    pickAnchor();
  });

  // Follow the reader, and wait out unfolds: the list element can change
  // between renders (locked, empty and filled states render different ones),
  // so this re-attaches each time. Reads only; nothing here renders.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const onUnfoldStart = (event: Event) => {
      if (isUnfold(event) && event.target) unfoldingRef.current.add(event.target);
    };
    const onUnfoldEnd = (event: Event) => {
      if (!isUnfold(event) || !event.target) return;
      unfoldingRef.current.delete(event.target);
      if (unfoldingRef.current.size === 0) pickAnchor();
    };
    list.addEventListener('scroll', pickAnchor, { passive: true });
    list.addEventListener('animationstart', onUnfoldStart);
    list.addEventListener('animationend', onUnfoldEnd);
    list.addEventListener('animationcancel', onUnfoldEnd);
    return () => {
      list.removeEventListener('scroll', pickAnchor);
      list.removeEventListener('animationstart', onUnfoldStart);
      list.removeEventListener('animationend', onUnfoldEnd);
      list.removeEventListener('animationcancel', onUnfoldEnd);
    };
  });
};

/**
 * Keeps a tapped card header exactly where it was on screen across the
 * re-render its tap causes. Opening a hand grows the card below the header;
 * opening one hand closes another, which may sit above and shrink; a pinned
 * header collapses back to its card's top. All of those used to move the
 * header under the finger (Jason, 28 Sep: "that header shouldn't move at
 * all"). The header's position is noted at the tap and restored by scrolling
 * the list by whatever it moved, after the layout that follows.
 */
export const useStableToggle = (listRef: RefObject<HTMLElement>) => {
  const pendingRef = useRef<{ element: HTMLElement; top: number } | null>(null);

  useLayoutEffect(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    const list = listRef.current;
    if (!list || !pending || !pending.element.isConnected) return;
    const delta = pending.element.getBoundingClientRect().top - pending.top;
    if (delta !== 0) list.scrollTop += delta;
  });

  return useCallback((element: HTMLElement | null | undefined) => {
    if (!element) return;
    pendingRef.current = { element, top: element.getBoundingClientRect().top };
  }, []);
};
